import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Select, TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { IntervalOption, PeriodOption } from '../../services/api';

const ACCENT = '#2391FF';
const SURFACE_BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(255,255,255,0.45)';

const selectSx = {
  color: '#fff',
  fontSize: 11,
  height: 30,
  borderRadius: '10px',
  bgcolor: 'rgba(255,255,255,0.04)',
  '& .MuiSelect-select': { py: '4px', pr: '28px !important' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: `${ACCENT}99` },
  '& .MuiSvgIcon-root': { color: MUTED, fontSize: 16, right: 4 },
};

const menuPaperSx = {
  backgroundColor: 'rgba(18,18,20,0.98)',
  border: `1px solid ${SURFACE_BORDER}`,
  borderRadius: '14px',
  mt: 0.5,
  '& .MuiMenuItem-root': {
    fontSize: 12,
    color: '#fff',
    borderRadius: '8px',
    mx: 0.5,
    my: 0.05,
    minHeight: 32,
    py: 0.45,
    '&:hover': { backgroundColor: 'rgba(35,145,255,0.14)' },
    '&.Mui-selected': { backgroundColor: 'rgba(35,145,255,0.22)' },
  },
};

const periodMenuPaperSx = {
  ...menuPaperSx,
  mt: 0,
  mb: 0.75,
  minWidth: 168,
  maxHeight: 'min(340px, 50vh)',
  '& .MuiMenuItem-root': {
    ...menuPaperSx['& .MuiMenuItem-root'],
    fontSize: 11.5,
    minHeight: 30,
    py: 0.35,
    my: 0,
  },
};

/** Por encima del modal de teléfono (1360) y de la barra inferior (1400). El menú de MUI sale en 1300. */
const menuLayerStyle = { zIndex: 1600 };

const periodMenuProps = {
  anchorOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  transformOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
  marginThreshold: 8,
  style: menuLayerStyle,
  PaperProps: { sx: periodMenuPaperSx },
};

const navBtnSx = {
  color: MUTED,
  p: '2px',
  borderRadius: '8px',
  border: '1px solid transparent',
  transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
  '&:hover': {
    color: '#fff',
    bgcolor: 'rgba(35,145,255,0.12)',
    borderColor: `${ACCENT}55`,
  },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
};

export type DeviceChartPeriodToolbarProps = {
  period: PeriodOption;
  chartInterval: IntervalOption;
  intervals: IntervalOption[];
  onPeriodChange: (p: PeriodOption) => void;
  onIntervalChange: (i: IntervalOption) => void;
  onNavigateDay: (dir: 'previous' | 'next') => void;
  onGoToday: () => void;
  onRefresh: () => void;
  canNavigateNextDay: boolean;
  isTodayActive: boolean;
  customDateDialogOpen: boolean;
  customStartInput: string;
  customEndInput: string;
  onCustomStartInputChange: (v: string) => void;
  onCustomEndInputChange: (v: string) => void;
  onCustomDateConfirm: () => void;
  onCustomDateCancel: () => void;
};

/** Barra período/intervalo/nav + diálogo custom — paridad Shelly DeviceModal. */
const DeviceChartPeriodToolbar: React.FC<DeviceChartPeriodToolbarProps> = ({
  period,
  chartInterval,
  intervals,
  onPeriodChange,
  onIntervalChange,
  onNavigateDay,
  onGoToday,
  onRefresh,
  canNavigateNextDay,
  isTodayActive,
  customDateDialogOpen,
  customStartInput,
  customEndInput,
  onCustomStartInputChange,
  onCustomEndInputChange,
  onCustomDateConfirm,
  onCustomDateCancel,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Box sx={{
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
        flexWrap: 'nowrap',
        mb: 1,
        minWidth: 0,
      }}>
        <FormControl size="small" sx={{ width: 148, flexShrink: 1, minWidth: 128 }}>
          <InputLabel sx={{ color: MUTED, fontSize: 11 }}>{t('deviceModal.period')}</InputLabel>
          <Select
            value={period}
            onChange={(e) => onPeriodChange(e.target.value as PeriodOption)}
            label={t('deviceModal.period')}
            sx={selectSx}
            MenuProps={periodMenuProps}
          >
            <MenuItem value="1hour">{t('reports.periods.1hour')}</MenuItem>
            <MenuItem value="6hours">{t('reports.periods.6hours')}</MenuItem>
            <MenuItem value="12hours">{t('reports.periods.12hours')}</MenuItem>
            <MenuItem value="24hours">{t('reports.periods.24hours')}</MenuItem>
            <MenuItem value="7days">{t('reports.periods.7days')}</MenuItem>
            <MenuItem value="30days">{t('reports.periods.30days')}</MenuItem>
            <MenuItem value="today">{t('reports.periods.today')}</MenuItem>
            <MenuItem value="yesterday">{t('reports.periods.yesterday')}</MenuItem>
            <MenuItem value="thisWeek">{t('reports.periods.thisWeek')}</MenuItem>
            <MenuItem value="thisMonth">{t('reports.periods.thisMonth')}</MenuItem>
            <MenuItem value="custom">{t('reports.periods.custom')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ width: 92, flexShrink: 1, minWidth: 80 }}>
          <InputLabel sx={{ color: MUTED, fontSize: 11 }}>{t('deviceModal.interval')}</InputLabel>
          <Select
            value={chartInterval}
            onChange={(e) => onIntervalChange(e.target.value as IntervalOption)}
            label={t('deviceModal.interval')}
            sx={selectSx}
            MenuProps={{ style: menuLayerStyle, PaperProps: { sx: menuPaperSx } }}
          >
            {intervals.map((intv) => (
              <MenuItem key={intv} value={intv}>{t(`reports.intervals.${intv}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0, ml: 'auto' }}>
          <IconButton size="small" onClick={() => onNavigateDay('previous')} sx={navBtnSx}>
            <ChevronLeft sx={{ fontSize: 17 }} />
          </IconButton>
          <IconButton size="small" onClick={onGoToday} disabled={isTodayActive} sx={navBtnSx}>
            <TodayIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onNavigateDay('next')}
            disabled={!canNavigateNextDay}
            sx={navBtnSx}
          >
            <ChevronRight sx={{ fontSize: 17 }} />
          </IconButton>
          <IconButton size="small" onClick={onRefresh} sx={navBtnSx}>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      <Dialog
        open={customDateDialogOpen}
        onClose={onCustomDateCancel}
        PaperProps={{
          style: {
            backgroundColor: 'rgba(18,18,20,0.98)',
            color: 'white',
            borderRadius: '16px',
            minWidth: '300px',
            border: '1px solid rgba(255,255,255,0.08)',
          },
        }}
      >
        <DialogTitle sx={{
          color: '#fff', fontSize: '1rem', fontWeight: 700,
          borderBottom: `1px solid ${SURFACE_BORDER}`, paddingBottom: 1,
        }}>
          {t('deviceModal.customPeriod')}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <TextField
            label={t('deviceModal.from')}
            type="datetime-local"
            value={customStartInput}
            onChange={(e) => onCustomStartInputChange(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInputLabel-root': { color: '#999' },
              '& .MuiInputBase-input': { color: '#fff' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER },
            }}
          />
          <TextField
            label={t('deviceModal.to')}
            type="datetime-local"
            value={customEndInput}
            onChange={(e) => onCustomEndInputChange(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInputLabel-root': { color: '#999' },
              '& .MuiInputBase-input': { color: '#fff' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px', borderTop: '1px solid #444' }}>
          <Button
            onClick={onCustomDateCancel}
            sx={{ color: '#999', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            {t('deviceModal.cancel')}
          </Button>
          <Button
            onClick={onCustomDateConfirm}
            sx={{ color: '#2391FF', fontWeight: 'bold', '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' } }}
          >
            {t('deviceModal.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeviceChartPeriodToolbar;
